import React from "react";
import { createRoot } from "react-dom/client";

import Neutralino, { init, events } from "@neutralinojs/lib";

import { App } from "./components/App";
import "./globals.css";
import { isDesktop } from "./hooks/useIsDesktop";

// Global flag to track Neutralino readiness
declare global {
  interface Window {
    __NEUTRALINO_READY__: boolean;
  }
}

const container = document.getElementById("app");
if (!container) {
  throw new Error("Failed to find root element with id 'app'");
}
const root = createRoot(container);

root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>,
);

if (isDesktop()) {
  console.log("Initializing Neutralino...", Neutralino);

  // Protect Neutralino globals from HMR interference
  if (import.meta.hot) {
    console.log("🔥 HMR detected - protecting Neutralino globals");
    import.meta.hot.accept(() => {
      // Prevent HMR from overriding Neutralino globals
      console.log("⚠️ HMR update - preserving Neutralino state");
    });
  }

  // Check if Neutralino globals are available
  const neutralinoGlobals = Object.keys(window).filter((key) =>
    key.startsWith("NL_"),
  );
  console.log("Available NL_ globals:", neutralinoGlobals.length);

  // Log the application ID that Neutralino is using
  console.log("Neutralino Application ID:", window.NL_APPID);
  console.log("Neutralino App Details:", {
    appId: window.NL_APPID,
    version: window.NL_VERSION,
    mode: window.NL_MODE,
    os: window.NL_OS,
  });

  // Set up ready event listener before calling init
  try {
    events.on("ready", () => {
      console.log("✅ Neutralino is ready!");

      // Try to manually set window.Neutralino from the import if needed
      if (!window.Neutralino && Neutralino) {
        console.log("⚠️ Manually setting window.Neutralino from import");
        window.Neutralino = Neutralino;
      }

      window.__NEUTRALINO_READY__ = true;

      // Dispatch a custom event to notify components that Neutralino is ready
      window.dispatchEvent(new CustomEvent("neutralino-ready"));
    });

    console.log("📡 Ready event listener set up, calling init()...");

    // Initialize Neutralino
    init();
  } catch (error) {
    console.error("❌ Error during Neutralino initialization:", error);
  }
} else {
  // If not desktop, mark as "ready" (not applicable)
  window.__NEUTRALINO_READY__ = false;
}
